import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Button, Grid, Typography } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { Box, Stack } from "@mui/system";
import { FullScreenPanel, LoadingGuard, ShowAccessDeniedMsg, useSafeSistaAsync, isProd, } from "@sista/library-stasi";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AlertsContext, DistaContent, } from "@sista/dista-ui";
import { useNavigate, useParams } from "react-router-dom";
import { nanoid } from "nanoid";
import { TextsTable } from "../parts/TextsTable";
import { NewNamespaceDialog } from "../dialogs/NewNamespaceDialog";
import { NewTextDialog } from "../dialogs/NewTextDialog";
import { TextDTOForm } from "../../forms/TextDTOForm";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
import { UploadFileDialog } from "../dialogs/UploadFileDialog";
import { SpratexTabs } from "../parts/SpratexTabs";
import { NamespaceTable } from "../parts/NamespaceTable";
import { UploadSummaryDialog } from "../dialogs/UploadSummaryDialog";
import { MassEditationDialog } from "../dialogs/MassEditationDialog";
import { RolesAdministration } from "../parts/RolesAdministration";
import { DictionaryTable } from "../parts/DictionaryTable";
import { IdmClientContext } from "@sista/idm-client";
const iconSize = { width: "20px", height: "20px" };
const timeoutValue = 500;
export const isProduction = isProd();
export const SpravaTextuAdminScreen = () => {
    const { t } = useTranslation("sprava-textu");
    const keyId = useParams().keyId;
    const id = useMemo(() => nanoid(), []);
    const view = useParams().view ?? "keys";
    const spratex = useSpravaTextuClient();
    const { loggedAccount } = useContext(IdmClientContext);
    const navigate = useNavigate();
    const [getPermissionsCall, getPermissionsState] = useSafeSistaAsync(spratex.getPermissions);
    const [getTextByKeyCall, getTextByKeyState] = useSafeSistaAsync(spratex.getText);
    const [selectedNamespace, setSelectedNamespace] = useState(null);
    const [selectedTexts, setSelectedTexts] = useState(null);
    const [editingText, setEditingText] = useState(null);
    const [editedAndSavedText, setEditedAndSavedText] = useState(null);
    const [showNewNamespaceDialog, setShowNewNamespaceDialog] = useState(false);
    const [showNewTextDialog, setShowNewTextDialog] = useState(false);
    const [showEditTextDialog, setShowEditTextDialog] = useState(false);
    const [showUploadFileDialog, setShowUploadFileDialog] = useState(false);
    const [showUploadSummaryDialog, setShowUploadSummaryDialog] = useState(false);
    const [showMassEditationDialog, setShowMassEditationDialog] = useState(false);
    const [importStats, setImportStats] = useState(null);
    const [namespacesChanged, setNamespacesChanged] = useState(false);
    const [isTextsChanged, setIsTextsChanged] = useState(false);
    const [isEditDone, setIsEditDone] = useState(false);
    const [isEditClosed, setIsEditClosed] = useState(false);
    const [getProviderProgramCfpTreeCall, getProviderProgramCfpTreeState] = useSafeSistaAsync(spratex.getProviderProgramCfpTree);
    useEffect(() => {
        getPermissionsCall({});
    }, [getPermissionsCall]);
    const canView = useMemo(() => {
        if (getPermissionsState.value == null)
            return false;
        return getPermissionsState.value.canView;
    }, [getPermissionsState.value]);
    const canEdit = useMemo(() => {
        if (getPermissionsState.value == null)
            return false;
        return getPermissionsState.value.canEdit;
    }, [getPermissionsState.value]);
    const canTranslate = useMemo(() => {
        if (getPermissionsState.value == null)
            return false;
        return getPermissionsState.value.canTranslate;
    }, [getPermissionsState.value]);
    const canViewRoles = useMemo(() => {
        if (getPermissionsState.value == null)
            return false;
        return getPermissionsState.value.canViewRoles;
    }, [getPermissionsState.value]);
    const canEditRoles = useMemo(() => {
        if (getPermissionsState.value == null)
            return false;
        return getPermissionsState.value.canEditRoles;
    }, [getPermissionsState.value]);
    useEffect(() => {
        if (getPermissionsState.value == null)
            return;
        if (canView || canEdit || canTranslate || canViewRoles || canEditRoles) {
            getProviderProgramCfpTreeCall({});
        }
    }, [
        getProviderProgramCfpTreeCall,
        getPermissionsState.value,
        canView,
        canEdit,
        canTranslate,
        canViewRoles,
        canEditRoles,
    ]);
    const tree = useMemo(() => {
        if (getProviderProgramCfpTreeState.value != null) {
            return getProviderProgramCfpTreeState.value;
        }
        return [];
    }, [getProviderProgramCfpTreeState.value]);
    const headerConfig = useMemo(() => {
        return {
            breadcrumbs: [
                {
                    title: t("sprava-textu.menu.breadcrumbs.home", "Domů"),
                    action: () => (window.location.href = "/ucet/"),
                },
                {
                    title: t("sprava-textu.admin.title", "Správa textů"),
                    action: () => (window.location.href = "/sprava-textu/ui/"),
                },
            ],
            pageTitle: t("sprava-textu.admin.title", "Správa textů"),
            chipsDefinition: [],
            actions: loggedAccount.providerId === "tacr" && canEdit
                ? [
                    {
                        title: t("sprava-textu.admin.newKey", "Vložit klíč").toLocaleUpperCase(),
                        action: () => {
                            setShowNewTextDialog(true);
                        },
                        primary: true,
                    },
                    {
                        title: t("sprava-textu.admin.uploadFile.buttonLabel", "Nahrát soubor"),
                        action: () => {
                            return setShowUploadFileDialog(true);
                        },
                        primary: false,
                    },
                    /*  {
                title: t("sprava-textu.admin.downloadFile", "Stáhnout soubor"),
                action: () => {
                  console.log("TODO: stahnout soubor");
                },
                primary: false,
              }, */
                    {
                        title: t("sprava-textu.admin.addNamespace", "Vytvořit nový prostor"),
                        action: () => {
                            setShowNewNamespaceDialog(true);
                        },
                        primary: false,
                    },
                    // {
                    //     title: t('mainpage.actions.primary', 'Primary action'),
                    //     action: () => alert('Primary action clicked'),
                    //     primary: true,
                    // },
                    // {title: t('mainpage.actions.action1', 'First action'), action: () => alert('First action clicked')},
                    // {title: t('mainpage.actions.action2', 'Second action'), action: () => alert('Second action clicked')},
                ]
                : [],
        };
    }, [t, canEdit]);
    useEffect(() => {
        if (view != null) {
            setEditingText(null);
            setSelectedTexts(null);
            setIsTextsChanged(false);
            setIsEditDone(false);
            setIsEditClosed(false);
            setSelectedNamespace(null);
            setShowEditTextDialog(false);
        }
    }, [view]);
    const setSelectedTextsHandler = useCallback((texts) => {
        return setSelectedTexts(texts);
    }, [setSelectedTexts]);
    const onEditTextHandler = useCallback((text) => {
        setEditedAndSavedText(null);
        if (text == null) {
            setIsTextsChanged(true);
            return setEditingText(null);
        }
        setSelectedTexts(null);
        setIsEditDone(false);
        setEditingText(text);
        setShowEditTextDialog(true);
    }, [setEditingText, setShowEditTextDialog]);
    useEffect(() => {
        if (!canView)
            return;
        if (view !== "keys" || keyId == null) {
            return;
        }
        const separatorIndex = keyId.indexOf("|");
        if (separatorIndex === -1) {
            return;
        }
        const namespace = keyId.slice(0, separatorIndex);
        const key = keyId.slice(separatorIndex + 1);
        if (editingText != null &&
            editingText.namespace === namespace &&
            editingText.key === key) {
            return;
        }
        getTextByKeyCall({ namespace, key }).then((result) => {
            if (result != null) {
                onEditTextHandler(result);
            }
        });
    }, [view, keyId, editingText, getTextByKeyCall, onEditTextHandler, canView]);
    const { showSuccess, showInfo } = useContext(AlertsContext);
    const [getLanguagesCall, getLanguagesState] = useSafeSistaAsync(spratex.getSupportedLanguages);
    useEffect(() => {
        if (getPermissionsState.value == null)
            return;
        if (canView || canEdit || canTranslate) {
            getLanguagesCall({});
        }
    }, [
        getLanguagesCall,
        getPermissionsState.value,
        canView,
        canEdit,
        canTranslate,
    ]);
    const [editTextCall, editTextState] = useSafeSistaAsync(spratex.updateText);
    const [deleteTextCall, deleteTextState] = useSafeSistaAsync(spratex.deleteText);
    /* const [resetingFormOnSucessForNextText, setResetingFormOnSucessForNextText] =
        useState(false); */
    const updateAndCloseHandler = useCallback(async (data) => {
        if (!data.namespace || !data.key) {
            showInfo(t("sprava-textu.admin.editText.missingNamespaceOrKey", "Chybí namespace nebo klíč"));
            return;
        }
        const result = await editTextCall({
            ...data,
            namespace: data.namespace,
            key: data.key,
        });
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.admin.editText.textUpdated", "Text aktualizován"), { timeout: timeoutValue });
            setEditedAndSavedText(result);
            setEditingText(null);
            setIsTextsChanged(true);
            setShowEditTextDialog(false);
        }
    }, [showSuccess, t, editTextCall, showInfo]);
    const updateAndMoreHandler = useCallback(async (data) => {
        const result = await editTextCall({
            ...data,
            namespace: data.namespace,
            key: data.key,
        });
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.admin.editText.textUpdated", "Text aktualizován"), {
                timeout: timeoutValue,
            });
            setEditedAndSavedText(result);
            setEditingText(null);
            setShowEditTextDialog(true);
            setIsEditDone(true);
        }
    }, [showSuccess, t, editTextCall]);
    const deleteTextHandler = useCallback(async (data) => {
        if (!data.namespace || !data.key) {
            showInfo(t("sprava-textu.admin.editText.missingNamespaceOrKey", "Chybí namespace nebo klíč"));
            return;
        }
        const result = await deleteTextCall({
            namespace: data.namespace,
            key: data.key,
        });
        if (result === true) {
            showSuccess(t("sprava-textu.admin.editText.textDeleted", "Text smazán"), { timeout: timeoutValue });
            setEditedAndSavedText(null);
            setIsEditClosed(true);
            setEditingText(null);
            setIsTextsChanged(true);
            setIsEditDone(false);
            setShowEditTextDialog(false);
            navigate("/keys/");
        }
    }, [showSuccess, t, deleteTextCall, showInfo, navigate]);
    return (_jsx(LoadingGuard, { state: getPermissionsState, children: canView ? (_jsx(DistaContent, { headerConfig: headerConfig, fullWidth: true, children: _jsxs(LoadingGuard, { state: getProviderProgramCfpTreeState, children: [!isProduction && (_jsx(Box, { sx: { mb: 2 }, children: _jsx(Alert, { severity: "warning", sx: { mb: 2 }, children: _jsx(Typography, { variant: "body1", children: t("sprava-textu.admin.testEnvironmentWarning", "Editujete texty v testovací verzi správy textů, vaše změny se nikde neprojeví. Všechny texty upravujte v produkční verzi správy textů.") }) }) })), showNewNamespaceDialog && (_jsx(NewNamespaceDialog, { onClose: () => {
                            return setShowNewNamespaceDialog(false);
                        }, onSuccess: () => {
                            return setNamespacesChanged(true);
                        } })), canEdit && showNewTextDialog && (_jsx(NewTextDialog, { canEdit: canEdit, canTranslate: canTranslate, canView: canView, tree: tree, supportedLanguages: getLanguagesState.value ?? null, onClose: () => {
                            return setShowNewTextDialog(false);
                        }, onSuccess: () => {
                            setIsTextsChanged(true);
                            return setNamespacesChanged(true);
                        } })), canEdit && showUploadFileDialog && (_jsx(UploadFileDialog, { supportedLanguages: getLanguagesState.value ?? null, onClose: () => {
                            return setShowUploadFileDialog(false);
                        }, onSuccess: ({ namespace, importStats }) => {
                            setShowUploadSummaryDialog(true);
                            setImportStats(importStats);
                            setSelectedNamespace(namespace);
                            return setShowUploadFileDialog(false);
                        } })), canEdit && showUploadSummaryDialog && importStats != null && (_jsx(UploadSummaryDialog, { importStats: importStats, onClose: () => {
                            setShowUploadSummaryDialog(false);
                            setImportStats(null);
                        } })), canEdit && tree && showMassEditationDialog && (_jsx(MassEditationDialog, { texts: selectedTexts, tree: tree, onClose: () => {
                            setShowMassEditationDialog(false);
                        }, onDone: () => {
                            setShowMassEditationDialog(false);
                            setIsTextsChanged(true);
                        } })), _jsx(FullScreenPanel
                    /* header={
                <Stack
                  direction={"row"}
                  spacing={2}
                  sx={{ my: 2, alignItems: "center" }}
                >
                  <SelectNamespace
                    isChanged={namespacesChanged}
                    multiple={true}
                    onNamespacesSelected={(namespaces) => {
                      if (typeof namespaces === "string") {
                        return setSelectedNamespace([namespaces]);
                      }
                      return setSelectedNamespace(namespaces);
                    }}
                    onRefresh={() => {
                      return setNamespacesChanged(false);
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    onClick={() => {
                      setShowNewNamespaceDialog(true);
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <PostAdd sx={iconSize} />
                      <Typography>
                        {t(
                          "sprava-textu.admin.addNamespace",
                          "Vytvořit nový prostor"
                        )}
                      </Typography>
                    </Stack>
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setShowNewTextDialog(true);
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Add sx={iconSize} />
                      <Typography>
                        {t("sprava-textu.admin.newKey", "Vložit klíč")}
                      </Typography>
                    </Stack>
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      console.log("TODO: nahrat soubor");
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Upload sx={iconSize} />
                      <Typography>
                        {t("sprava-textu.admin.uploadFile", "Nahrát soubor")}
                      </Typography>
                    </Stack>
                  </Button>
                </Stack>
              } */
                    , { 
                        /* header={
                    <Stack
                      direction={"row"}
                      spacing={2}
                      sx={{ my: 2, alignItems: "center" }}
                    >
                      <SelectNamespace
                        isChanged={namespacesChanged}
                        multiple={true}
                        onNamespacesSelected={(namespaces) => {
                          if (typeof namespaces === "string") {
                            return setSelectedNamespace([namespaces]);
                          }
                          return setSelectedNamespace(namespaces);
                        }}
                        onRefresh={() => {
                          return setNamespacesChanged(false);
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        variant="contained"
                        onClick={() => {
                          setShowNewNamespaceDialog(true);
                        }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <PostAdd sx={iconSize} />
                          <Typography>
                            {t(
                              "sprava-textu.admin.addNamespace",
                              "Vytvořit nový prostor"
                            )}
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setShowNewTextDialog(true);
                        }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <Add sx={iconSize} />
                          <Typography>
                            {t("sprava-textu.admin.newKey", "Vložit klíč")}
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          console.log("TODO: nahrat soubor");
                        }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <Upload sx={iconSize} />
                          <Typography>
                            {t("sprava-textu.admin.uploadFile", "Nahrát soubor")}
                          </Typography>
                        </Stack>
                      </Button>
                    </Stack>
                  } */
                        spaceAbove: 160, main: _jsxs(Stack, { direction: "column", spacing: 2, sx: { height: "100%" }, children: [_jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(SpratexTabs, { canViewRoles: canViewRoles }), _jsx(Box, { sx: { flexGrow: 1 } }), canEdit &&
                                            selectedTexts != null &&
                                            selectedTexts.length > 0 ? (_jsxs(Stack, { direction: "row", spacing: 2, sx: { my: 2, alignItems: "center" }, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", onClick: () => {
                                                        navigate("/");
                                                        //console.log("TODO: editovat vybrane texty:", selectedTexts);
                                                        setShowMassEditationDialog(true);
                                                    }, children: _jsxs(Stack, { direction: "row", spacing: 1, sx: { alignItems: "center" }, children: [_jsx(Edit, { sx: iconSize }), _jsx(Typography, { children: t("sprava-textu.admin.editSelected", "Editovat vybrané") })] }) })] })) : null] }), view === "keys" && (_jsxs(Grid, { container: true, spacing: 2, sx: { height: "calc(100% - 70px)", width: "100%" }, children: [_jsx(Grid, { item: true, xs: 12, md: showEditTextDialog ? 4 : 12, sx: { height: "100%" }, children: _jsx(TextsTable, { canEdit: canEdit, isSmall: showEditTextDialog, isChanged: isTextsChanged, isClosed: isEditClosed, editedText: editedAndSavedText, onRefresh: () => {
                                                    setSelectedNamespace(null);
                                                    setIsEditClosed(false);
                                                    return setIsTextsChanged(false);
                                                }, isEditDone: isEditDone, selectedNamespaces: selectedNamespace, onSelectedChange: setSelectedTextsHandler, onEditText: onEditTextHandler, isRowSelectable: !showEditTextDialog }) }), _jsx(Grid, { item: true, xs: 12, md: showEditTextDialog ? 8 : 0, sx: { height: "100%", overflow: "auto" }, children: _jsx(LoadingGuard, { state: getLanguagesState, children: getLanguagesState.value &&
                                                    showEditTextDialog &&
                                                    editingText != null ? (_jsx(TextDTOForm, { canEdit: canEdit, canTranslate: canTranslate, canView: canView, supportedLanguages: getLanguagesState.value ?? null, textData: editingText ?? null, tree: tree, selectedNamespaces: [
                                                        editingText?.namespace ?? null,
                                                    ], onSuccess: (data) => updateAndCloseHandler(data), onTextDelete: (data) => deleteTextHandler(data), onClose: () => {
                                                        setIsEditClosed(true);
                                                        setEditingText(null);
                                                        setIsTextsChanged(true);
                                                        setIsEditDone(false);
                                                        setShowEditTextDialog(false);
                                                        navigate("/keys/");
                                                    }, onMoreTexts: (data) => updateAndMoreHandler(data) })) : null }) })] })), view === "namespaces" && (_jsx(Grid, { container: true, spacing: 2, sx: { height: "calc(100% - 70px)", width: "100%" }, children: _jsx(Grid, { item: true, xs: 12, sx: { height: "100%", mb: 1 }, children: _jsx(NamespaceTable, { canEdit: canEdit }) }) })), view === "dictionary" && (_jsx(Grid, { container: true, spacing: 2, sx: { height: "calc(100% - 70px)", width: "100%" }, children: _jsx(Grid, { item: true, xs: 12, sx: { height: "100%", mb: 1 }, children: _jsx(DictionaryTable, { canEdit: canEdit }) }) })), view === "roles" && (_jsx(Grid, { container: true, spacing: 2, sx: { height: "calc(100% - 70px)", width: "100%" }, children: _jsx(Grid, { item: true, xs: 12, sx: { height: "100%", mb: 1 }, children: _jsx(RolesAdministration, { tree: tree, canEditRoles: canEditRoles, canViewRoles: canViewRoles }) }) }))] }) })] }) })) : (_jsx(ShowAccessDeniedMsg, {})) }));
};
